import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logoIconPng from '../logo-icon.svg';

const DEFAULT_LAST_UPDATED = '11/05/2026';
const COMPANY_NAME = 'Lumix Óticas';
const RESPONSIBLE_NAME = 'Responsável pela plataforma Lumix Óticas';
const CONTACT_EMAIL = 'contato@lumixoticas.com.br';

const legalLinks = [
  { to: '/termos-de-uso', label: 'Termos' },
  { to: '/condicoes-comerciais', label: 'Comercial' },
  { to: '/politica-de-privacidade', label: 'Privacidade' },
  { to: '/politica-de-cookies', label: 'Cookies' }
];

export default function LegalLayout({
  eyebrow,
  title,
  description,
  children,
  lastUpdated = DEFAULT_LAST_UPDATED,
  summaryLinks = []
}) {
  const location = useLocation();

  // Garante que ao trocar de termo, a página suba para o topo
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="lumix-legal-page">
      <style>{css}</style>

      {/* HEADER FIXO COM BOTÃO FECHAR */}
      <header className="legal-nav-header">
        <div className="legal-wrap legal-nav-inner">
          <Link to="/" className="legal-brand">
            <div className="logo-mark-wrap">
              <img src={logoIconPng} alt="Lumix" />
            </div>
            <span className="brand-name">
              Lumix&nbsp;
              <span style={{color:'#4285F4'}}>Ó</span>
              <span style={{color:'#EA4335'}}>t</span>
              <span style={{color:'#FBBC04'}}>i</span>
              <span style={{color:'#34A853'}}>c</span>
              <span style={{color:'#4285F4'}}>a</span>
              <span style={{color:'#EA4335'}}>s</span>
            </span>
          </Link>

          <nav className="legal-desktop-nav">
            {legalLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`legal-nav-lnk ${location.pathname === link.to ? 'is-active' : ''}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="legal-nav-end">
            <Link to="/" className="btn-close-legal" title="Voltar para o início">
              <span>Fechar</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </Link>
          </div>
        </div>
      </header>

      <main className="legal-main-content">
        {/* HERO */}
        <section className="legal-hero">
          <div className="legal-wrap">
            <span className="legal-chip-blue">{eyebrow}</span>
            <h1 className="legal-h1">{title}</h1>
            <p className="legal-sub">{description}</p>

            {/* NAVEGAÇÃO MOBILE (CARROSSEL DE LINKS) */}
            <div className="legal-mobile-switcher">
              {legalLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`mob-switcher-btn ${location.pathname === link.to ? 'active' : ''}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            <div className="legal-meta-tags">
              <span className="meta-tag">Atualizado em: {lastUpdated}</span>
              <span className="meta-tag">{COMPANY_NAME}</span>
            </div>
          </div>
        </section>

        {/* CONTEÚDO COM SIDEBAR */}
        <section className="legal-content-section">
          <div className="legal-wrap legal-grid">
            {summaryLinks.length > 0 && (
              <aside className="legal-sidebar">
                <div className="sidebar-card">
                  <strong>Neste documento</strong>
                  <nav>
                    {summaryLinks.map((item) => (
                      <a key={item.href} href={item.href}>{item.label}</a>
                    ))}
                  </nav>
                </div>
              </aside>
            )}

            <article className="legal-article card">
              {children}
            </article>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="legal-footer">
        <div className="legal-wrap">
          <div className="footer-top">
            <div className="footer-brand">
              <img src={logoIconPng} alt="Lumix" />
              <span>
                Lumix&nbsp;
                <span style={{color:'#4285F4'}}>Ó</span>
                <span style={{color:'#EA4335'}}>t</span>
                <span style={{color:'#FBBC04'}}>i</span>
                <span style={{color:'#34A853'}}>c</span>
                <span style={{color:'#4285F4'}}>a</span>
                <span style={{color:'#EA4335'}}>s</span>
              </span>
            </div>
            <p>{RESPONSIBLE_NAME} · {CONTACT_EMAIL}</p>
          </div>
          
          <div className="footer-bottom">
            <div className="footer-copy">
              © {new Date().getFullYear()} Lumix Óticas. Todos os direitos reservados.
            </div>
            <div className="footer-links">
              <Link to="/">Página Inicial</Link>
              <Link to="/login">Login</Link>
              <Link to="/checkout/start">Planos</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Nunito:wght@700;800;900&display=swap');

  :root {
    --primary: #1A73E8;
    --primary-hover: #1765CC;
    --primary-soft: #EEF5FF;

    --text-main: #171A21;
    --text-sec: #626A78;
    --text-soft: #9098A5;

    --bg: #F7F9FC;
    --surface: #FFFFFF;
    --surface-soft: #FAFBFC;

    --border: #E3E8EF;
    --border-strong: #D3DAE5;

    --green: #34A853;
    --red: #EA4335;
    --yellow: #FBBC04;

    --shadow-sm: 0 1px 2px rgba(15,23,42,.04), 0 1px 3px rgba(15,23,42,.03);
    --shadow-md: 0 12px 36px rgba(30,41,59,.07);

    --font-ui: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    --font-brand: 'Nunito', 'Inter', system-ui, sans-serif;
  }

  *, *::before, *::after {
    box-sizing: border-box;
  }

  html {
    scroll-behavior: smooth;
    background: var(--bg);
  }

  body {
    margin: 0;
  }

  .lumix-legal-page {
    min-height: 100vh;
    background:
      radial-gradient(circle at 8% 0%, rgba(26,115,232,.045), transparent 26rem),
      var(--bg);
    color: var(--text-main);
    font-family: var(--font-ui);
    line-height: 1.65;
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
  }

  .legal-wrap {
    width: min(1120px, calc(100% - 48px));
    margin: 0 auto;
  }

  /* HEADER */
  .legal-nav-header {
    position: sticky;
    top: 0;
    z-index: 1000;
    height: 68px;
    display: flex;
    align-items: center;
    background: rgba(255,255,255,.86);
    border-bottom: 1px solid rgba(227,232,239,.92);
    backdrop-filter: blur(18px) saturate(150%);
  }

  .legal-nav-inner {
    height: 100%;
    display: flex;
    align-items: center;
    gap: 24px;
  }

  .legal-brand {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    flex: 0 0 auto;
    color: var(--text-main);
    text-decoration: none;
  }

  .logo-mark-wrap {
    width: 30px;
    height: 30px;
    display: grid;
    place-items: center;
    flex: 0 0 auto;
  }

  .logo-mark-wrap img {
    width: 30px;
    height: 30px;
    object-fit: contain;
  }

  .brand-name {
    font-family: var(--font-brand);
    font-size: 19px;
    line-height: 1;
    font-weight: 900;
    letter-spacing: -.035em;
    white-space: nowrap;
  }

  .legal-desktop-nav {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .legal-nav-lnk {
    min-height: 36px;
    padding: 0 12px;
    display: inline-flex;
    align-items: center;
    border-radius: 10px;
    color: var(--text-sec);
    text-decoration: none;
    font-size: 12px;
    font-weight: 650;
    transition: color .16s ease, background .16s ease;
  }

  .legal-nav-lnk:hover {
    color: var(--text-main);
    background: #F4F6F8;
  }

  .legal-nav-lnk.is-active {
    color: var(--primary);
    background: var(--primary-soft);
    font-weight: 800;
  }

  .legal-nav-end {
    margin-left: 8px;
    flex: 0 0 auto;
  }

  .btn-close-legal {
    min-height: 36px;
    padding: 0 11px;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    border: 1px solid var(--border);
    border-radius: 10px;
    background: #fff;
    color: var(--text-sec);
    text-decoration: none;
    font-size: 11px;
    font-weight: 700;
    transition: border-color .16s ease, background .16s ease, color .16s ease, transform .16s ease;
    box-shadow: var(--shadow-sm);
  }

  .btn-close-legal:hover {
    color: var(--text-main);
    background: #F8FAFC;
    border-color: var(--border-strong);
    transform: translateY(-1px);
  }

  /* HERO */
  .legal-main-content {
    min-height: calc(100vh - 68px);
  }

  .legal-hero {
    padding: 64px 0 44px;
    border-bottom: 1px solid var(--border);
    background: rgba(255,255,255,.58);
  }

  .legal-chip-blue {
    display: inline-flex;
    align-items: center;
    min-height: 26px;
    padding: 0 10px;
    margin-bottom: 15px;
    border-radius: 999px;
    background: var(--primary-soft);
    color: var(--primary);
    font-size: 10px;
    line-height: 1;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: .075em;
  }

  .legal-h1 {
    max-width: 820px;
    margin: 0 0 12px;
    color: var(--text-main);
    font-family: var(--font-brand);
    font-size: clamp(34px, 5vw, 48px);
    line-height: 1.04;
    font-weight: 900;
    letter-spacing: -.045em;
  }

  .legal-sub {
    max-width: 720px;
    margin: 0 0 22px;
    color: var(--text-sec);
    font-size: 14px;
    line-height: 1.7;
    font-weight: 500;
  }

  .legal-meta-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .meta-tag {
    min-height: 28px;
    padding: 0 10px;
    display: inline-flex;
    align-items: center;
    border: 1px solid var(--border);
    border-radius: 9px;
    background: #fff;
    color: var(--text-sec);
    font-size: 10px;
    font-weight: 650;
    box-shadow: var(--shadow-sm);
  }

  /* MOBILE SWITCHER */
  .legal-mobile-switcher {
    display: none;
    gap: 8px;
    overflow-x: auto;
    overscroll-behavior-inline: contain;
    scrollbar-width: none;
    padding-bottom: 4px;
    margin-bottom: 18px;
  }

  .legal-mobile-switcher::-webkit-scrollbar {
    display: none;
  }

  .mob-switcher-btn {
    min-height: 36px;
    padding: 0 13px;
    display: inline-flex;
    align-items: center;
    flex: 0 0 auto;
    border: 1px solid var(--border);
    border-radius: 10px;
    background: #fff;
    color: var(--text-sec);
    text-decoration: none;
    font-size: 11px;
    font-weight: 700;
  }

  .mob-switcher-btn.active {
    color: var(--primary);
    background: var(--primary-soft);
    border-color: #C9DCF8;
  }

  /* CONTENT */
  .legal-content-section {
    padding: 46px 0 70px;
  }

  .legal-grid {
    display: grid;
    grid-template-columns: 220px minmax(0, 1fr);
    gap: 42px;
    align-items: flex-start;
  }

  .legal-sidebar {
    min-width: 0;
  }

  .sidebar-card {
    position: sticky;
    top: 94px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 13px;
    border: 1px solid var(--border);
    border-radius: 16px;
    background: rgba(255,255,255,.82);
    box-shadow: var(--shadow-sm);
    backdrop-filter: blur(10px);
  }

  .sidebar-card strong {
    color: var(--text-soft);
    font-size: 9px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: .08em;
  }

  .sidebar-card nav {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .sidebar-card a {
    padding: 7px 8px;
    border-radius: 8px;
    color: var(--text-sec);
    text-decoration: none;
    font-size: 11px;
    line-height: 1.4;
    font-weight: 600;
    transition: background .15s ease, color .15s ease, transform .15s ease;
  }

  .sidebar-card a:hover {
    color: var(--primary);
    background: var(--primary-soft);
    transform: translateX(2px);
  }

  .legal-article {
    min-width: 0;
    padding: 38px 42px;
    border: 1px solid var(--border);
    border-radius: 22px;
    background: rgba(255,255,255,.96);
    box-shadow: var(--shadow-md);
  }

  .legal-article > *:first-child {
    margin-top: 0 !important;
  }

  .legal-article h1,
  .legal-article h2,
  .legal-article h3,
  .legal-article h4 {
    color: var(--text-main);
    font-family: var(--font-brand);
    letter-spacing: -.025em;
  }

  .legal-article h2 {
    margin: 34px 0 12px;
    padding-top: 4px;
    font-size: 22px;
    line-height: 1.25;
    font-weight: 850;
  }

  .legal-article h3 {
    margin: 27px 0 10px;
    font-size: 16px;
    line-height: 1.3;
    font-weight: 800;
  }

  .legal-article p,
  .legal-article li {
    color: #464D59;
    font-size: 13px;
    line-height: 1.75;
    font-weight: 450;
  }

  .legal-article p {
    margin: 0 0 15px;
  }

  .legal-article ul,
  .legal-article ol {
    margin: 12px 0 18px;
    padding-left: 22px;
  }

  .legal-article li {
    margin-bottom: 8px;
  }

  .legal-article strong {
    color: var(--text-main);
    font-weight: 750;
  }

  .legal-article a {
    color: var(--primary);
    text-decoration: none;
    font-weight: 650;
  }

  .legal-article a:hover {
    text-decoration: underline;
  }

  .legal-article hr {
    height: 1px;
    margin: 30px 0;
    border: 0;
    background: var(--border);
  }

  .legal-article blockquote {
    margin: 20px 0;
    padding: 14px 16px;
    border-left: 3px solid var(--primary);
    border-radius: 0 12px 12px 0;
    background: var(--primary-soft);
    color: var(--text-sec);
  }

  /* FOOTER */
  .legal-footer {
    padding: 38px 0 26px;
    border-top: 1px solid var(--border);
    background: rgba(255,255,255,.72);
    color: var(--text-sec);
  }

  .footer-top {
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    flex-wrap: wrap;
  }

  .footer-brand {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: var(--text-main);
  }

  .footer-brand img {
    width: 25px;
    height: 25px;
    object-fit: contain;
  }

  .footer-brand > span {
    font-family: var(--font-brand);
    font-size: 16px;
    line-height: 1;
    font-weight: 900;
    letter-spacing: -.03em;
  }

  .footer-top p {
    margin: 0;
    color: var(--text-soft);
    font-size: 10px;
    line-height: 1.5;
    font-weight: 550;
  }

  .footer-bottom {
    padding-top: 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    flex-wrap: wrap;
    border-top: 1px solid var(--border);
  }

  .footer-copy {
    color: var(--text-soft);
    font-size: 10px;
    font-weight: 550;
  }

  .footer-links {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .footer-links a {
    color: var(--text-sec);
    text-decoration: none;
    font-size: 10px;
    font-weight: 650;
  }

  .footer-links a:hover {
    color: var(--primary);
  }

  @media (max-width: 900px) {
    .legal-wrap {
      width: min(760px, calc(100% - 32px));
    }

    .legal-desktop-nav {
      display: none;
    }

    .legal-mobile-switcher {
      display: flex;
    }

    .legal-grid {
      grid-template-columns: 1fr;
    }

    .legal-sidebar {
      display: none;
    }

    .legal-content-section {
      padding-top: 28px;
    }

    .legal-article {
      padding: 30px;
    }
  }

  @media (max-width: 600px) {
    .legal-nav-header {
      height: 60px;
    }

    .legal-wrap {
      width: calc(100% - 24px);
    }

    .legal-nav-inner {
      gap: 12px;
    }

    .logo-mark-wrap,
    .logo-mark-wrap img {
      width: 27px;
      height: 27px;
    }

    .brand-name {
      font-size: 17px;
    }

    .btn-close-legal {
      width: 36px;
      min-height: 36px;
      padding: 0;
      justify-content: center;
    }

    .btn-close-legal span {
      display: none;
    }

    .legal-hero {
      padding: 40px 0 28px;
    }

    .legal-chip-blue {
      margin-bottom: 12px;
    }

    .legal-h1 {
      font-size: 31px;
    }

    .legal-sub {
      font-size: 12px;
      line-height: 1.65;
      margin-bottom: 18px;
    }

    .legal-meta-tags {
      gap: 6px;
    }

    .meta-tag {
      min-height: 26px;
      font-size: 9px;
    }

    .legal-content-section {
      padding: 18px 0 44px;
    }

    .legal-article {
      padding: 22px 18px;
      border-radius: 17px;
      box-shadow: var(--shadow-sm);
    }

    .legal-article h2 {
      margin-top: 28px;
      font-size: 19px;
    }

    .legal-article h3 {
      font-size: 15px;
    }

    .legal-article p,
    .legal-article li {
      font-size: 12px;
      line-height: 1.72;
    }

    .footer-top {
      align-items: flex-start;
      flex-direction: column;
      gap: 12px;
    }

    .footer-bottom {
      align-items: flex-start;
      flex-direction: column;
    }

    .footer-links {
      flex-wrap: wrap;
      gap: 12px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      scroll-behavior: auto !important;
      animation-duration: .01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: .01ms !important;
    }
  }
`;
